import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { API_URLS, API_CONFIG } from '../../../config/api';
import { Upload, Search, Trash2, Download, Eye, Image as ImageIcon, FileImage, Link as LinkIcon, RefreshCw, Video, Play, Pause, Link2 } from 'lucide-react';
import { useWebPConverter } from '../../../hooks/useWebPConverter';

const AdminMedia = () => {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [shotUrl, setShotUrl] = useState('');
  const [shotLoading, setShotLoading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, per_page: 50, total: 0, total_pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [normalizingUrls, setNormalizingUrls] = useState(false);
  
  // WebP converter hook
  const { convertMultipleToWebP, converting: webpConverting } = useWebPConverter();

  // WebP browser support check (once on mount)
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'red';
    ctx.fillRect(0, 0, 1, 1);
    
    canvas.toBlob((blob) => {
      if (blob && blob.type === 'image/webp') {
        console.log('✅ Tarayıcı WebP formatını destekliyor');
      } else {
        console.warn('⚠️  Tarayıcı WebP formatını desteklemiyor veya canvas.toBlob() çalışmıyor');
      }
    }, 'image/webp', 1.0);
  }, []);

  useEffect(() => {
    fetchMedia(currentPage);
  }, [currentPage]);

  const fetchMedia = async (page = 1) => {
    try {
      setLoading(true);
      const url = `${API_URLS.ADMIN.MEDIA}&page=${page}&per_page=50`;
      const response = await fetch(url);
      if (response.ok) {
        const responseText = await response.text();
        try {
          const data = JSON.parse(responseText);
          setMediaFiles(data.data || []);
          if (data.pagination) {
            setPagination(data.pagination);
          }
        } catch (parseError) {
          // Silent error handling
        }
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    
    try {
      // Resim dosyalarını WebP'ye dönüştür (JPEG, PNG, GIF -> WebP)
      // Video dosyaları ve zaten WebP olan dosyalar olduğu gibi kalır
      const filesArray = Array.from(files);
      console.log('📤 Yüklenecek dosyalar:', filesArray.map(f => `${f.name} (${f.type})`));
      const convertedFiles = await convertMultipleToWebP(filesArray, 1.0); // 100% quality
      console.log('📥 Dönüştürülen dosyalar:', convertedFiles.map(f => `${f.name} (${f.type})`));
      
      const uploadPromises = convertedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await fetch(API_URLS.ADMIN.UPLOAD_MEDIA, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });

          const result = await response.json().catch(() => ({}));
          if (response.ok && result.status === 'success') {
            return result.data;
          }
          const msg = result.message || result.detail || `Upload failed (${response.status})`;
          throw new Error(msg);
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          alert(`Yükleme hatası (${file.name}): ${error.message || String(error)}`);
          return null;
        }
      });

      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(result => result !== null);
      
      if (successfulUploads.length > 0) {
        alert(`${successfulUploads.length} file(s) uploaded successfully!`);
        fetchMedia(currentPage); // Refresh the media list
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert(`Upload error: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    handleFileUpload(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDelete = async (filename) => {
    if (!confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URLS.ADMIN.DELETE_MEDIA}&filename=${filename}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          fetchMedia(currentPage);
          alert('Dosya silindi!');
        } else {
          alert('Hata: ' + (result.message || 'Bilinmeyen hata'));
        }
      } else {
        alert('Hata: Sunucu hatası');
      }
    } catch (error) {
      alert('Hata: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <ImageIcon className="h-4 w-4" />;
    }
    if (mimeType.startsWith('video/')) {
      return <Video className="h-4 w-4" />;
    }
    return <FileImage className="h-4 w-4" />;
  };

  const allowedMediaTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif',
    'video/mp4', 'video/webm', 'video/ogg', 'video/avi', 'video/mov', 'video/wmv'
  ];
  
  // Filter out non-media files and PHP files
  const filteredFiles = mediaFiles.filter(file => {
    const isAllowedType = allowedMediaTypes.includes(file.type);
    const isNotPhpFile = !file.name.toLowerCase().endsWith('.php');
    const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return isAllowedType && isNotPhpFile && matchesSearch;
  });

  const normalize = (url) => {
    // If URL already starts with http, return as is
    if (url.startsWith('http')) {
      return url;
    }
    // If URL starts with /, prepend BASE_URL
    if (url.startsWith('/')) {
      return `${API_CONFIG.BASE_URL}${url}`;
    }
    // Otherwise, assume it's a relative path
    return `${API_CONFIG.BASE_URL}/${url}`;
  };

  const captureScreenshot = async (force = false) => {
    if (!shotUrl) return;
    setShotLoading(true);
    try {
      const res = await fetch(API_URLS.ADMIN.ADMIN_API + '?action=captureScreenshotToMedia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: shotUrl, force })
      });
      const data = await res.json();
      if (res.ok && (data.status === 'success')) {
        alert('Ekran görüntüsü kaydedildi.');
        await fetchMedia(currentPage);
        setShotUrl('');
      } else {
        alert(data.message || 'Ekran görüntüsü alınamadı');
      }
    } catch (e) {
      alert('Hata: ' + e.message);
    } finally {
      setShotLoading(false);
    }
  };

  const handleNormalizeMediaUrls = async () => {
    if (!confirm('Tüm veritabanı ve önbellekteki resim URL\'leri o anki site adresiyle değiştirilecek (lokalde localhost:3000, canlıda website-al.com). Devam edilsin mi?')) return;
    setNormalizingUrls(true);
    try {
      const res = await fetch(API_URLS.ADMIN.ADMIN_API + '?action=normalizeMediaUrls', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        alert(data.message || 'URL\'ler güncellendi.');
        fetchMedia(currentPage);
      } else {
        alert(data.message || 'Güncelleme yapılamadı.');
      }
    } catch (e) {
      alert('Hata: ' + e.message);
    } finally {
      setNormalizingUrls(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Medya Yönetimi</h1>
        <Button
          variant="outline"
          onClick={handleNormalizeMediaUrls}
          disabled={normalizingUrls}
          className="flex items-center gap-2"
        >
          <Link2 className="h-4 w-4" />
          {normalizingUrls ? 'Güncelleniyor...' : 'Resim URL\'lerini site adresiyle güncelle'}
        </Button>
      </div>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Dosya Yükle
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-2">
              Dosyaları buraya sürükleyin veya tıklayın
            </p>
            <p className="text-sm text-gray-500 mb-4">
              PNG, JPG, JPEG, WebP, GIF, MP4, WebM, OGG, AVI, MOV, WMV formatları desteklenir (Maksimum 50MB)
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || webpConverting}
              className="mb-2"
            >
              {uploading || webpConverting 
                ? (webpConverting ? 'WebP\'ye dönüştürülüyor...' : 'Yükleniyor...') 
                : 'Dosya Seç'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/png,image/jpg,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/ogg,video/avi,video/mov,video/wmv"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      {/* ScreenshotMachine Capture */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            URL'den Ekran Görüntüsü Al ve Kütüphaneye Kaydet
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-3">
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="https://ornek.com"
                  value={shotUrl}
                  onChange={(e) => setShotUrl(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => captureScreenshot(true)} disabled={shotLoading || !shotUrl} className="flex items-center gap-2 w-full md:w-auto">
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Ekran görüntüsü al</span>
                <span className="sm:hidden">Yakala</span>
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">URL girin, ScreenshotMachine ile ekran görüntüsü alınır ve doğrudan /uploads içine kaydedilir.</p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Medya dosyalarında ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredFiles.map((file) => (
          <Card key={file.name} className="overflow-hidden">
            <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
              {file.type.startsWith('image/') ? (
                <img
                  src={normalize(file.url)}
                  alt={file.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : file.type.startsWith('video/') ? (
                <div className="w-full h-full relative group">
                  <video
                    src={normalize(file.url)}
                    className="w-full h-full object-cover"
                    controls={playingVideo === file.name}
                    onPlay={() => setPlayingVideo(file.name)}
                    onPause={() => setPlayingVideo(null)}
                    onEnded={() => setPlayingVideo(null)}
                  />
                  {playingVideo !== file.name && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="rounded-full p-3"
                        onClick={() => {
                          const video = document.querySelector(`video[src="${normalize(file.url)}"]`);
                          if (video) {
                            video.play();
                            setPlayingVideo(file.name);
                          }
                        }}
                      >
                        <Play className="h-6 w-6" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  {getFileTypeIcon(file.type)}
                  <p className="text-xs text-gray-500 mt-2">Dosya</p>
                </div>
              )}
              
              {/* File type badge */}
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" className="text-xs">
                  {file.type.startsWith('image/') ? (
                    <ImageIcon className="h-3 w-3 mr-1" />
                  ) : file.type.startsWith('video/') ? (
                    <Video className="h-3 w-3 mr-1" />
                  ) : (
                    <FileImage className="h-3 w-3 mr-1" />
                  )}
                  {file.type.startsWith('image/') ? 'Resim' : file.type.startsWith('video/') ? 'Video' : 'Dosya'}
                </Badge>
              </div>
            </div>
            <CardContent className="p-3">
              <div className="space-y-2">
                <p className="text-sm font-medium truncate" title={file.name}>
                  {file.name}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{formatFileSize(file.size)}</span>
                  <span>{new Date(file.uploaded_at).toLocaleDateString('tr-TR')}</span>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => window.open(normalize(file.url), '_blank')}
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = normalize(file.url);
                      link.download = file.name;
                      link.click();
                    }}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(file.name)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredFiles.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileImage className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-500">
            {searchTerm ? 'Arama kriterlerinize uygun dosya bulunamadı' : 'Henüz yüklenmiş dosya yok'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {searchTerm ? 'Farklı bir arama terimi deneyin' : 'İlk dosyanızı yüklemek için yukarıdaki alanı kullanın'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-600">
            Toplam {pagination.total} dosya, Sayfa {pagination.page} / {pagination.total_pages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              Önceki
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage >= pagination.total_pages || loading}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
